import React, { useCallback, useMemo } from "react";
import { IdPClientRoot, useIdPClient } from "./client";
import { IDP_CALLBACK_PATH } from "./constants";
import { IdPAuthContext, IdPAuthContextInterface } from "./IdPAuthContext";
import { AppState, IdPLogoutOptions, IdPUser, LoginWithIdPOptions } from "./types";
import { usePrepareLogin } from "./usePrepareLogin";

export interface IdPAuthProviderOptions {
  children: React.ReactNode;
  context?: React.Context<IdPAuthContextInterface>;

  // IdP tenant info
  domain: string;
  clientId: string;
  audience: string;

  // Proxy Domain info for Authorize call
  proxyDomainForAuthorize?: string;

  /**
   * Called once the IdP client has handled the callback route.
   */
  onRedirectCallback: (appState?: AppState, user?: IdPUser) => void;
}

const callbackUrl = () =>
  (typeof window !== "undefined" && `${window.location.origin}${IDP_CALLBACK_PATH}`) || "";

/**
 * Mounts the IdP client and provides the IdP session through IdPAuthContext.
 * Sibling of SendGridSessionProvider: this one owns the IdP half of a unified login.
 */
export const IdPAuthProvider = ({
  children,
  onRedirectCallback,
  ...opts
}: IdPAuthProviderOptions): JSX.Element => {
  const redirectUri = callbackUrl();
  return (
    <IdPClientRoot
      domain={opts.domain}
      clientId={opts.clientId}
      audience={opts.audience}
      redirectUri={redirectUri}
      onRedirectCallback={onRedirectCallback}>
      <IdPAuthProviderImpl {...opts} redirectUri={redirectUri}>
        {children}
      </IdPAuthProviderImpl>
    </IdPClientRoot>
  );
};

interface IdPAuthProviderImplOptions extends Omit<IdPAuthProviderOptions, "onRedirectCallback"> {
  redirectUri: string;
}

const IdPAuthProviderImpl = (opts: IdPAuthProviderImplOptions): JSX.Element => {
  const {
    children,
    context = IdPAuthContext,
    domain,
    clientId,
    audience,
    proxyDomainForAuthorize,
    redirectUri,
  } = opts;
  const { client, isAuthenticated, isLoading, user } = useIdPClient({
    domain,
    clientId,
    audience,
    proxyDomainForAuthorize,
    redirectUri,
  });
  const prepareLogin = usePrepareLogin(audience);

  const loginWithRedirect = useCallback(
    async (opts: LoginWithIdPOptions = {}) => {
      await client.loginWithRedirect(await prepareLogin(opts));
    },
    [client, prepareLogin]
  );

  const getTokensSilently = useCallback(() => client.getTokensSilently(), [client]);

  const logout = useCallback((opts: IdPLogoutOptions) => client.logout(opts), [client]);

  const contextValue = useMemo(() => {
    return {
      isAuthenticated,
      isLoading,
      user,
      loginWithRedirect,
      getTokensSilently,
      logout,
    };
  }, [isAuthenticated, isLoading, user, loginWithRedirect, getTokensSilently, logout]);
  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
